<script>
import { ref } from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables/wgpt';
import FullEditor from '../../components/FullEditor.vue';
import { trimEditorContent } from '../../helpers/editorContent';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import canDismissMixin from '../../mixins/preventDismissMixin';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { emitter } from 'shared/helpers/mitt';
import getUuid from 'widget/helpers/uuid';

import { OnClickOutside } from '@vueuse/components';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ButtonNext from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    FullEditor,
    Icon,
    ButtonNext,
    OnClickOutside,
  },
  mixins: [canDismissMixin],
  props: {
    board: {
      type: Object,
      default: () => {},
    },
    list: {
      type: Object,
      default: () => {},
    },
    card: {
      type: Object,
      default: () => {},
    },
    readonly: {
      type: Boolean,
      default: true,
    },
    agents: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const { formatMessage } = useMessageFormatter();

    const id = getUuid();
    const content = ref('');
    const contentEditorVisible = ref(false);

    const showMessageEditor = () => {
      if (props.readonly) return;
      content.value = (content.value || props.card.description) ?? '';

      emitter.emit('wgptCloseAllBoardListCardActivityEditor');
      contentEditorVisible.value = true;
      emitter.emit('WGPT_DROPDOWN_TOGGLE', { id, state: true });
    };
    const hideMessageEditor = persistInput => {
      const ProseMirrorPrompt = document.querySelector('.ProseMirror-prompt');
      if (ProseMirrorPrompt) return;
      contentEditorVisible.value = false;
      emitter.emit('WGPT_DROPDOWN_TOGGLE', { id, state: false });
      if (!persistInput) {
        content.value = '';
      }
    };

    useKeyboardEvents({
      Escape: {
        action: () => hideMessageEditor(),
        allowOnFocusedInput: true,
      },
    });

    return {
      formatMessage,
      id,
      content,
      contentEditorVisible,
      showMessageEditor,
      hideMessageEditor,
    };
  },
  data() {
    return {
      isUploading: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'wgptBoardListCards/getUIFlags',
    }),
    placeholder() {
      return this.$t(
        this.readonly
          ? 'WGPT_BOARDS.ITEM.LIST.CARD.EDIT.DESCRIPTION.EMPTY'
          : 'WGPT_BOARDS.ITEM.LIST.CARD.EDIT.DESCRIPTION.PLACEHOLDER'
      );
    },
    preventDismiss() {
      return this.contentEditorVisible;
    },
  },
  methods: {
    async onSubmit() {
      try {
        const boardId = this.board.id;
        const listId = this.list.id;
        const id = this.card.id;
        const data = {
          description: trimEditorContent(this.content),
        };
        await this.$store.dispatch('wgptBoardListCards/update', {
          boardId,
          listId,
          id,
          ...data,
        });

        this.hideMessageEditor();
      } catch (error) {
        const errorMessage =
          error.message ||
          this.$t(
            'WGPT_BOARDS.ITEM.LIST.CARD.EDIT.DESCRIPTION.API.ERROR_MESSAGE'
          );
        useAlert(errorMessage);
      }
    },
    async uploadFileToStorage(file) {
      const data = {
        file,
        boardId: this.board.id,
        listId: this.list.id,
        id: this.card.id,
      };
      const fileInfo = await this.$store.dispatch(
        'wgptBoardListCards/addAttachment',
        data
      );
      return fileInfo.file_url;
    },
    onFileUploadStart() {
      this.isUploading = true;
    },
    onFileUploadSuccess() {
      this.isUploading = false;
    },
    onFileUploadError() {
      this.isUploading = false;
    },
  },
};
</script>

<template>
  <div>
    <div class="flex gap-6">
      <Icon
        icon="i-lucide-align-left"
        class="shrink-0 mt-1 w-5 h-5 text-slate-500"
      />
      <div class="flex-1 min-w-0 text-wrap">
        <label class="mb-1">
          {{ $t('WGPT_BOARDS.ITEM.LIST.CARD.EDIT.DESCRIPTION.LABEL') }}
        </label>

        <div v-if="contentEditorVisible">
          <OnClickOutside @trigger="hideMessageEditor(true)">
            <FullEditor
              v-model="content"
              :placeholder="placeholder"
              :upload-file="uploadFileToStorage"
              :agents="agents"
              @file-upload-start="onFileUploadStart"
              @file-upload-success="onFileUploadSuccess"
              @file-upload-error="onFileUploadError"
            />

            <div class="flex gap-2 mt-2">
              <ButtonNext
                :label="$t('WGPT_BOARDS.ITEM.LIST.CARD.EDIT.DESCRIPTION.SAVE')"
                :disabled="uiFlags.isCreating || isUploading"
                :is-loading="uiFlags.isCreating"
                size="sm"
                @click="onSubmit"
              />
              <ButtonNext
                :is-disabled="uiFlags.isCreating"
                size="sm"
                variant="faded"
                color="slate"
                @click="() => hideMessageEditor()"
              >
                {{ $t('WGPT_BOARDS.ITEM.LIST.CARD.EDIT.DESCRIPTION.CANCEL') }}
              </ButtonNext>
            </div>
          </OnClickOutside>
        </div>
        <label v-else @click="showMessageEditor">
          <div
            v-if="card.description"
            v-dompurify-html="formatMessage(card.description)"
            class="dompurified-html min-h-[3.5em] shadow px-3 py-2 rounded-lg bg-n-alpha-black2 break-words [&>p]:text-slate-900 [&>p]:dark:text-slate-300 [&>p:first-child]:mt-0"
            :class="{ 'cursor-pointer': !readonly }"
            dir="auto"
          />
          <div
            v-else
            class="min-h-[3.5em] px-3 py-1 rounded-lg cursor-pointer bg-n-alpha-black2 text-slate-400 dark:text-slate-500 shadow"
          >
            {{ placeholder }}
          </div>
        </label>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dompurified-html {
  ::v-deep {
    ul {
      @apply pl-4;
    }
    .prosemirror-mention-node {
      @apply bg-none rounded-sm p-0 bg-n-alpha-black1 underline;
    }
  }
}
</style>
